import type { ReactNode } from 'react';
import { useId } from 'react';
import type { ChartGranularity } from '@/lib/chart-axis';
import type { ChartRange } from '@/lib/partial-buckets';
import type { DeltaView } from '@/lib/period-comparison';
import { DeltaBadge } from '@/components/metrics/delta-badge';
import {
  HomeSparkline,
  type SparklineRow,
} from '@/components/home/home-sparkline';
import { HelpIconButton } from '@/components/help-panel/help-icon-button';
import { LongArrow } from '@/components/brand/long-arrow';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

export interface OverviewCardRow {
  label: string;
  value: string;
}

export interface OverviewCardProps {
  /** "01" … "04", shown before the title. */
  index: string;
  title: string;
  helpKey: string;
  /** Name of the headline number. */
  label: string;
  /** Headline number; `null` while loading. */
  value: string | null;
  /** Variation against the previous period (CMP-01…03). */
  delta?: DeltaView;
  /** "vs. …" next to the variation. */
  comparison?: ReactNode;
  /** Minigráfico of the period, partial bucket dashed (CHART-02). */
  spark?: {
    rows: SparklineRow[];
    range: ChartRange;
    granularity: ChartGranularity;
  };
  rows: OverviewCardRow[];
  /** "Ver vendas", … */
  cta: string;
  onCta: () => void;
}

/**
 * One card of the Resumo tab (ANLT-02): the area's headline number with its
 * variation and minigráfico, three secondary numbers and a shortcut to the
 * area's tab.
 */
export function OverviewCard({
  index,
  title,
  helpKey,
  label,
  value,
  delta,
  comparison,
  spark,
  rows,
  cta,
  onCta,
}: OverviewCardProps) {
  const titleId = useId();

  return (
    <Card
      role="region"
      aria-labelledby={titleId}
      data-overview-card
      className="flex min-w-0 flex-col gap-3.5 p-5 max-md:p-4"
    >
      <div className="flex items-center gap-2">
        <h3
          id={titleId}
          className="flex flex-1 items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
        >
          <span aria-hidden="true" className="text-[#155DFC]">
            {index}
          </span>
          <span>{title}</span>
          <span aria-hidden="true" className="h-px flex-1 bg-border" />
        </h3>
        <HelpIconButton helpKey={helpKey} />
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-muted-foreground">
            {label}
          </span>
          {value === null ? (
            <Skeleton className="h-9 w-32" />
          ) : (
            <span
              data-overview-value
              className="text-3xl font-semibold leading-tight tracking-[-0.025em] tabular-nums max-md:text-2xl"
            >
              {value}
            </span>
          )}
          {delta ? (
            <div className="min-h-5">
              <DeltaBadge delta={delta} comparison={comparison} />
            </div>
          ) : null}
        </div>
        {spark ? (
          <HomeSparkline
            rows={spark.rows}
            range={spark.range}
            granularity={spark.granularity}
            className="h-14 w-auto min-w-0 max-w-60 flex-[1_1_160px]"
          />
        ) : null}
      </div>

      <dl
        className="grid gap-3 border-t pt-3.5"
        style={{
          gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))`,
        }}
      >
        {rows.map((row) => (
          <div key={row.label} className="flex min-w-0 flex-col gap-0.5">
            <dt className="text-xs text-muted-foreground">{row.label}</dt>
            <dd className="truncate text-[15px] font-semibold tabular-nums">
              {row.value}
            </dd>
          </div>
        ))}
      </dl>

      <button
        type="button"
        onClick={onCta}
        className="group inline-flex min-h-11 w-fit items-center gap-3 text-sm font-semibold text-[#155DFC] hover:text-[#0B4AE0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span>{cta}</span>
        <LongArrow lineClassName="w-[22px] group-hover:w-10" />
      </button>
    </Card>
  );
}
